import React, { useCallback, useEffect, useRef } from "react";
import classNames from "classnames";
import { ImageUploader } from "antd-mobile";
import { isAndroid, isHarmony } from "../../utils/common";
import { useVoiceToText } from "./hooks/useVoiceToText";
import PreviewImages from "../PreviewImages";
import VideoPlayer from "../../pages/vehicle/report/components/MediaList/VideoPlayer";
import MessageBox from "../message-box";
import {
  useAttachmentUpload,
  UploadItem,
} from "../AttachmentUpload/useAttachmentUpload";
import attachmentStyles from "../AttachmentUpload/index.module.scss";
import voiceIcon from "./img/voice.png";
import voiceUploadIcon from "./img/mcActive.svg";
import uploadIcon from "./img/upload.svg";
import styles from "./index.module.scss";

interface TextareaBoxProps {
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  /** 失焦回调（如埋点上报），组件不写死业务逻辑 */
  onBlur?: (value: string) => void;
  /** 最大字数，默认 500 */
  maxLength?: number;
  /** 只读态：纯文本展示已填写内容，不可编辑、不显示字数（无内容显示「—」） */
  readOnly?: boolean;
  /** 是否开启原生语音转文字输入，默认关闭以保持其他调用方行为不变 */
  enableVoiceInput?: boolean;
  /** 必填标记：在 placeholder 前展示红色 * （#FF4D4F），仅在编辑态且未填写内容时显示 */
  required?: boolean;
  /** 校验未通过时标红边框提示 */
  error?: boolean;
  /** 是否开启附件上传（底部「上传图片/视频」胶囊 + 框外缩略图），默认关闭 */
  enableAttachment?: boolean;
  /** 附件 fileId 列表（受控，enableAttachment 时生效） */
  attachmentValue?: string[];
  /** 附件变更回调 */
  onAttachmentChange?: (fileIds: string[]) => void;
  /** 附件最大数量，默认 9 */
  attachmentMaxCount?: number;
  /** 附件文件选择 accept，默认 image/*,video/* */
  attachmentAccept?: string;
}

/**
 * 通用多行文本输入框（灰底 + 字数统计 + 键盘适配）。
 * 底部操作区为胶囊按钮：语音输入（enableVoiceInput）/ 上传图片/视频（enableAttachment）；
 * 附件缩略图展示在框外下方。语音状态由 useVoiceToText、附件逻辑由 useAttachmentUpload 管理。
 */
export default function TextareaBox(props: TextareaBoxProps) {
  const {
    placeholder,
    value = "",
    onChange,
    onBlur,
    maxLength = 500,
    readOnly = false,
    enableVoiceInput = false,
    required = false,
    error = false,
    enableAttachment = false,
    attachmentValue,
    onAttachmentChange,
    attachmentMaxCount,
    attachmentAccept = "image/*,video/*",
  } = props;
  const boxRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const voiceButtonRef = useRef<HTMLButtonElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const focusedRef = useRef(false);
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showVoiceNotice = useCallback((message: string) => {
    MessageBox.toast(message);
  }, []);
  const {
    phase,
    isRequestingPermission,
    isTextLocked,
    handleVoicePressStart,
    handleVoicePressEnd,
  } = useVoiceToText({
      enabled: enableVoiceInput,
      value,
      maxLength,
      textareaRef,
      onChange,
      onNotice: showVoiceNotice,
    });

  // 附件上传逻辑（无条件调用以遵循 hooks 规则；enableAttachment=false 时 value 为空、不渲染上传 UI）
  const {
    items: attachItems,
    emit: emitAttach,
    doUpload: uploadAttach,
    handleFilesSelected,
    handlePreview,
    renderItem: renderAttachItem,
    previewRef,
    videoUrl,
    videoVisible,
    setVideoVisible,
    uploading: attachUploading,
    maxCount: attachMaxCount,
  } = useAttachmentUpload({
    value: attachmentValue,
    onChange: onAttachmentChange,
    maxCount: attachmentMaxCount,
  });

  // 安卓 / 鸿蒙键盘弹起时把当前聚焦的输入框滚到视口中央，避免被键盘遮挡。
  useEffect(() => {
    if (!(isAndroid() || isHarmony())) return;
    const onResize = () => {
      if (!focusedRef.current) return;
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
      scrollTimerRef.current = setTimeout(() => {
        if (focusedRef.current && boxRef.current) {
          boxRef.current.scrollIntoView({
            block: "center",
            behavior: "smooth",
          });
        }
      }, 300);
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    };
  }, []);

  // React 16 将 touch 事件委托到 document，部分 WebView 会强制按 passive 监听处理，
  // 导致 React 事件里的 preventDefault 无法可靠阻止原生长按选区；因此直接在按钮上注册非被动监听。
  useEffect(() => {
    const button = voiceButtonRef.current;
    if (!button) return;

    const preventNativeLongPress = (event: TouchEvent) => {
      event.preventDefault();
    };
    const listenerOptions: AddEventListenerOptions = { passive: false };

    button.addEventListener("touchstart", preventNativeLongPress, listenerOptions);
    button.addEventListener("touchmove", preventNativeLongPress, listenerOptions);

    return () => {
      button.removeEventListener("touchstart", preventNativeLongPress);
      button.removeEventListener("touchmove", preventNativeLongPress);
    };
  }, [enableVoiceInput, readOnly]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (e?.target?.value === undefined) return;
    const next =
      e.target.value.length <= maxLength
        ? e.target.value
        : e.target.value.slice(0, maxLength);
    onChange?.(next);
  };

  const handleFocus = () => {
    focusedRef.current = true;
  };

  const handleBlur = () => {
    focusedRef.current = false;
    if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    onBlur?.(value || "");
  };

  const handleTextareaPointerDown = (
    event: React.PointerEvent<HTMLDivElement>
  ) => {
    if (!isTextLocked || (event.target as HTMLElement).closest("button")) {
      return;
    }
    event.preventDefault();
    MessageBox.toast(
      phase === "recording"
        ? "正在语音输入，请松手结束"
        : "正在识别语音，请稍后"
    );
  };

  const onPickFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) handleFilesSelected(e.target.files);
    // 清空以允许再次选择相同文件
    e.target.value = "";
  };

  const len = (value || "").length;

  // 附件缩略图列表（框外，编辑/只读共用；只读态不可删）
  const renderThumbnails = () => {
    if (!enableAttachment || attachItems.length === 0) return null;
    return (
      <div className={styles.attachmentThumbs}>
        <div className={attachmentStyles.attachmentField}>
          <ImageUploader
            value={attachItems}
            onChange={(next) => emitAttach(next as UploadItem[])}
            upload={uploadAttach}
            showUpload={false}
            deletable={!readOnly}
            preview={false}
            onPreview={handlePreview}
            renderItem={renderAttachItem}
          />
        </div>
      </div>
    );
  };

  // 附件预览层（图片预览 + 视频播放），编辑/只读共用
  const attachmentPortals = enableAttachment ? (
    <>
      <PreviewImages ref={previewRef} />
      {videoVisible && (
        <VideoPlayer src={videoUrl} onClose={() => setVideoVisible(false)} />
      )}
    </>
  ) : null;

  if (readOnly) {
    return (
      <div ref={boxRef}>
        <div className={`${styles.box} ${styles.boxReadonly}`}>
          <textarea className={styles.textarea} value={value || ""} readOnly />
        </div>
        {renderThumbnails()}
        {attachmentPortals}
      </div>
    );
  }

  const showActionBar = enableVoiceInput || enableAttachment;

  return (
    <div ref={boxRef}>
      <div
        className={classNames(styles.box, { [styles.boxError]: error })}
        onPointerDownCapture={handleTextareaPointerDown}
      >
        {required && !value && <span className={styles.requiredStar}>*</span>}
        <textarea
          ref={textareaRef}
          className={classNames(styles.textarea, {
            [styles.textareaLocked]: isTextLocked,
            [styles.textareaRequired]: required && !value,
          })}
          placeholder={placeholder}
          value={value || ""}
          disabled={isTextLocked}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
        <div
          className={classNames(styles.count, {
            [styles.warning]: len >= maxLength,
          })}
        >
          {len}/{maxLength}
        </div>

        {showActionBar && (
          <div className={styles.actionBar}>
            {enableVoiceInput && (
              <button
                ref={voiceButtonRef}
                type="button"
                className={classNames(styles.pillBtn, styles.pillBtnVoice, {
                  [styles.pillBtnRecording]: phase === "recording",
                  [styles.pillBtnInitializing]: isRequestingPermission,
                })}
                aria-label={
                  isRequestingPermission
                    ? "语音准备中"
                    : phase === "editing"
                    ? "按住说话"
                    : "松手结束语音输入"
                }
                aria-disabled={
                  isRequestingPermission || phase === "recognizing"
                }
                onPointerDown={(e) => {
                  // 阻止按钮抢占焦点与 iOS 长按弹出系统菜单/文本选择
                  e.preventDefault();
                  handleVoicePressStart(e.pointerId);
                }}
                onPointerUp={(e) => handleVoicePressEnd(e.pointerId)}
                onPointerCancel={(e) => handleVoicePressEnd(e.pointerId)}
                onPointerLeave={(e) => handleVoicePressEnd(e.pointerId)}
                onContextMenu={(e) => e.preventDefault()}
              >
                <img
                  className={styles.pillIcon}
                  src={phase === "recording" ? voiceUploadIcon : voiceIcon}
                  alt=""
                />
                <span>
                  {isRequestingPermission
                    ? "语音准备中..."
                    : phase === "editing"
                    ? "按住说话"
                    : "语音转文字"}
                </span>
              </button>
            )}
            {enableAttachment && (
              <button
                type="button"
                className={styles.pillBtn}
                aria-label="上传图片/视频"
                disabled={attachUploading}
                onClick={() => {
                  if (attachUploading) return; // 上传中禁止再次选择
                  // 已达上限：点击即提示，不弹文件选择器
                  if (attachItems.length >= attachMaxCount) {
                    MessageBox.toast(`最多上传 ${attachMaxCount} 个`);
                    return;
                  }
                  fileInputRef.current?.click();
                }}
              >
                <img src={uploadIcon} className={styles.pillPlus} alt="" />
                <span>{attachUploading ? "上传中..." : "上传图片/视频"}</span>
              </button>
            )}
          </div>
        )}
      </div>

      {enableAttachment && (
        <input
          ref={fileInputRef}
          type="file"
          accept={attachmentAccept}
          multiple
          style={{ display: "none" }}
          onChange={onPickFiles}
        />
      )}

      {renderThumbnails()}
      {attachmentPortals}
    </div>
  );
}
